import type { Platform, Project, ProjectLifecycle } from "@szyos/contracts";
import {
  MAX_FOCUS_PROJECTS,
  OPEN_PORTFOLIO_GROUPS,
  PORTFOLIO_GROUPS,
  comparePortfolio,
  nextStep,
  portfolioGroup,
  projectOverview,
  type PortfolioGroup,
  type ProjectActivity,
} from "@szyos/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { api, errorMessage, type WorkOrderSummary } from "../api";
import { EmptyState, ErrorNote, useToast } from "../components/feedback";
import { dateTime, orderStatus } from "../format";
import { DeliveryPanel } from "../components/Delivery";
import { StocktakePanel } from "../components/Stocktake";
import { DoneBadge, DonePanel, FocusControl, NextStepLink } from "../components/Focus";
import { MaterialPanel } from "../components/MaterialPanel";
import { ProjectRolloutPanel } from "../components/ProjectRollout";
import { Icon, type IconName } from "../icons";
import { PLATFORMS } from "./Ideas";

export const LIFECYCLE_LABEL: Record<ProjectLifecycle, string> = {
  development: "In Entwicklung",
  operating: "Im Betrieb",
  closed: "Abgeschlossen",
};

const ACTIVITY: Record<ProjectActivity, { label: string; icon: IconName }> = {
  needs_you: { label: "Braucht dich", icon: "alert" },
  working: { label: "In Arbeit", icon: "activity" },
  waiting: { label: "Wartet", icon: "clock" },
  held: { label: "Angehalten", icon: "clock" },
  quiet: { label: "Ruhig", icon: "clock" },
};

const GROUP_LABEL: Record<PortfolioGroup, string> = {
  focus: "Im Fokus",
  needs_you: "Braucht dich",
  running: "Läuft",
  parked: "Geparkt",
  operating: "Im Betrieb",
  closed: "Abgeschlossen",
};

/**
 * Projektübersicht (24.10.2026): eine kompakte Zeile je Projekt, gruppiert wie in „Für dich zu tun“. Stand je
 * Projekt aus den Aufträgen (packages/domain/src/portfolio.ts); Ziel und Fakten stehen auf der Projektseite.
 */
function PortfolioList({
  projects,
  orders,
  loading,
}: {
  projects: Project[];
  orders: WorkOrderSummary[];
  loading: boolean;
}) {
  const rows = projects
    .map((p) => {
      const own = orders.filter((o) => o.projectId === p.id);
      return { project: p, overview: projectOverview(p.createdAt, own), step: nextStep(p, own) };
    })
    .sort((a, b) => comparePortfolio(a.overview, b.overview));
  const groups = PORTFOLIO_GROUPS.map((g) => ({
    key: g,
    rows: rows.filter((r) => portfolioGroup(r.project.lifecycle, r.overview.activity, r.project.focus) === g),
  })).filter((g) => g.rows.length > 0);
  return (
    <div className="project-list">
      {projects.length === 0 && !loading && (
        <EmptyState
          icon="folder"
          title="Noch keine Projekte"
          text="Lege ein Projekt an oder mache eine reife Idee zum Projekt."
        />
      )}
      {projects.length > 0 && !projects.some((p) => p.focus) && (
        <p className="muted">
          Noch kein Projekt im Fokus. Wähle bis zu {MAX_FOCUS_PROJECTS} Projekte, die du fertigstellen willst.
        </p>
      )}
      {groups.map((g) => (
        <details key={g.key} className="todo-group portfolio-group" open={OPEN_PORTFOLIO_GROUPS.has(g.key)}>
          <summary>
            <strong>{GROUP_LABEL[g.key]}</strong> <span className="muted">({g.rows.length})</span>
          </summary>
          <ul className="portfolio-rows">
            {g.rows.map(({ project: p, overview: o, step }) => (
              <li key={p.id} className="portfolio-row">
                <span className="portfolio-head">
                  <Link to="/projects/$projectId" params={{ projectId: p.id }} className="portfolio-title">
                    <strong>{p.title}</strong>
                  </Link>
                  <span className={`chip portfolio-activity is-${o.activity}`}>
                    <Icon name={ACTIVITY[o.activity].icon} /> {ACTIVITY[o.activity].label}
                  </span>
                  <DoneBadge project={p} />
                </span>
                <span className="portfolio-step">
                  <NextStepLink step={step} projectId={p.id} compact />
                  <span className="muted"> · zuletzt {dateTime(o.lastActivityAt)}</span>
                </span>
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

export function Projects({ autoFocus }: { autoFocus: boolean }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const workOrders = useQuery({ queryKey: ["work-orders"], queryFn: api.workOrders });
  const [title, setTitle] = useState("");
  const [goal, setGoal] = useState("");
  const [platform, setPlatform] = useState<Platform>("web_pwa");
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const ids = { title: useId(), goal: useId(), platform: useId() };
  // Das Formular klappt erst auf Wunsch auf, damit die Liste oben beginnt (24.10.2026).
  const [formOpen, setFormOpen] = useState(autoFocus);
  useEffect(() => {
    if (autoFocus) setFormOpen(true);
  }, [autoFocus]);
  useEffect(() => {
    if (formOpen) titleRef.current?.focus();
  }, [formOpen]);

  const create = useMutation({
    mutationFn: () =>
      api.createProject({
        title,
        goal,
        platform,
        profileKey: PLATFORMS.find((p) => p.value === platform)?.profile ?? "generic",
      }),
    onSuccess: async (p) => {
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      void navigate({ to: "/projects/$projectId", params: { projectId: p.id } });
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !goal.trim()) {
      setError("Titel und Ziel angeben.");
      return;
    }
    create.mutate();
  };

  return (
    <>
      <section className="hero">
        <div className="hero-text">
          <h1>
            Projekte <span className="glow-word">im Blick.</span>
          </h1>
          <p className="hero-sub">
            Alle Projekte mit ihrem echten Stand. Höchstens {MAX_FOCUS_PROJECTS} sind im Fokus, jedes zeigt
            genau einen nächsten Schritt. Fertigstellen geht vor Anfangen.
          </p>
          <p>
            <Link to="/review" className="link">
              <Icon name="clock" /> Wochenrückblick
            </Link>
          </p>
        </div>
        <div className="hero-actions">
          <button
            type="button"
            className="btn"
            aria-expanded={formOpen}
            aria-controls="newProjectForm"
            onClick={() => setFormOpen((o) => !o)}
          >
            <span className="btn-label">
              <Icon name={formOpen ? "x" : "plus"} /> {formOpen ? "Schließen" : "Neues Projekt"}
            </span>
          </button>
        </div>
      </section>
      <section className="projects-layout">
        {formOpen && (
          <form
            id="newProjectForm"
            className="panel form capture"
            onSubmit={submit}
            noValidate
            aria-labelledby="newProject"
          >
            <h2 id="newProject">Neues Projekt</h2>
            <label htmlFor={ids.title}>Titel</label>
            <input
              id={ids.title}
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
            />
            <label htmlFor={ids.goal}>Ziel in einem Satz</label>
            <textarea
              id={ids.goal}
              rows={2}
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              maxLength={2000}
            />
            <label htmlFor={ids.platform}>Plattform und Profil</label>
            <select
              id={ids.platform}
              value={platform}
              onChange={(e) => setPlatform(e.target.value as Platform)}
            >
              {PLATFORMS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label} (Profil {p.profile})
                </option>
              ))}
            </select>
            <ErrorNote error={error} />
            <button type="submit" className="btn btn--primary" disabled={create.isPending}>
              <span className="btn-label">
                <Icon name="plus" /> Anlegen
              </span>
            </button>
          </form>
        )}
        <PortfolioList
          projects={projects.data ?? []}
          orders={workOrders.data ?? []}
          loading={projects.isLoading}
        />
      </section>
    </>
  );
}

/** Lebensphase direkt auf der Projektseite wählen; „abgeschlossen“ geht erst ohne offene Aufträge. */
function LifecycleControl({ project }: { project: Project }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const id = useId();
  const [value, setValue] = useState<ProjectLifecycle>(project.lifecycle);
  const save = useMutation({
    mutationFn: (lifecycle: ProjectLifecycle) => api.setProjectLifecycle(project.id, lifecycle),
    onSuccess: async (p) => {
      queryClient.setQueryData(["project", project.id], p);
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast(`Lebensphase: ${LIFECYCLE_LABEL[p.lifecycle]}`);
    },
    onError: (err) => {
      setValue(project.lifecycle);
      toast(errorMessage(err), "error");
    },
  });
  return (
    <span className="lifecycle-control">
      <label htmlFor={id}>Lebensphase</label>
      <select
        id={id}
        value={value}
        disabled={save.isPending}
        onChange={(e) => {
          const next = e.target.value as ProjectLifecycle;
          setValue(next);
          save.mutate(next);
        }}
      >
        {(Object.keys(LIFECYCLE_LABEL) as ProjectLifecycle[]).map((l) => (
          <option key={l} value={l}>
            {LIFECYCLE_LABEL[l]}
          </option>
        ))}
      </select>
    </span>
  );
}

/** Titel und Ziel eines Projekts ändern. */
function ProjectEditForm({ project, onDone }: { project: Project; onDone: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState(project.title);
  const [goal, setGoal] = useState(project.goal);
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const ids = { title: useId(), goal: useId() };

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const save = useMutation({
    mutationFn: () => api.updateProject(project.id, { title, goal }),
    onSuccess: async (p) => {
      queryClient.setQueryData(["project", project.id], p);
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast("Projekt gespeichert");
      onDone();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !goal.trim()) return setError("Titel und Ziel angeben.");
    save.mutate();
  };

  return (
    <form className="panel form capture" onSubmit={submit} noValidate aria-labelledby="editProjectHead">
      <h2 id="editProjectHead">Projekt bearbeiten</h2>
      <label htmlFor={ids.title}>Titel</label>
      <input
        id={ids.title}
        ref={titleRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={200}
      />
      <label htmlFor={ids.goal}>Ziel in einem Satz</label>
      <textarea
        id={ids.goal}
        value={goal}
        onChange={(e) => setGoal(e.target.value)}
        rows={3}
        maxLength={2000}
      />
      <ErrorNote error={error} />
      <div className="idea-actions">
        <button type="submit" className="btn btn--primary" disabled={save.isPending}>
          <span className="btn-label">Speichern</span>
        </button>
        <button type="button" className="btn" onClick={onDone} disabled={save.isPending}>
          <span className="btn-label">Abbrechen</span>
        </button>
      </div>
    </form>
  );
}

export function ProjectDetail({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [orderForm, setOrderForm] = useState(false);
  const project = useQuery({ queryKey: ["project", projectId], queryFn: () => api.project(projectId) });
  const workOrders = useQuery({ queryKey: ["work-orders"], queryFn: api.workOrders });
  const [title, setTitle] = useState("");
  const [budget, setBudget] = useState("5");
  const [error, setError] = useState<string | null>(null);
  const ids = { title: useId(), budget: useId(), repo: useId() };
  const titleRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [repo, setRepo] = useState("");
  const [repoError, setRepoError] = useState<string | null>(null);
  const setRepository = useMutation({
    mutationFn: (fullName: string | null) => api.setProjectRepository(projectId, fullName),
    onSuccess: (p) => {
      setRepo("");
      setRepoError(null);
      queryClient.setQueryData(["project", projectId], p);
      toast(p.repoFullName ? `Repo ${p.repoFullName} zugeordnet.` : "Repo-Zuordnung gelöst.");
    },
    onError: (err) => setRepoError(errorMessage(err)),
  });
  const submitRepo = (e: FormEvent) => {
    e.preventDefault();
    if (!/^[A-Za-z0-9-]+\/[A-Za-z0-9._-]+$/.test(repo.trim())) {
      return setRepoError("Repo als owner/name angeben, z. B. medienrocker/meine-app.");
    }
    setRepository.mutate(repo.trim());
  };
  const stocktake = useMutation({
    mutationFn: () => api.startStocktake(projectId),
    onSuccess: async (wo) => {
      await queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      toast("Bestandsaufnahme angelegt. Prüfe das Briefing und gib es frei.");
      void navigate({ to: "/work-orders/$orderId", params: { orderId: wo.id } });
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const create = useMutation({
    mutationFn: () =>
      api.createWorkOrder(projectId, { title, kind: "feature", budgetUsd: Number(budget.replace(",", ".")) }),
    onSuccess: (wo) => {
      toast("Auftrag angelegt. Jetzt das Briefing schreiben.");
      void navigate({ to: "/work-orders/$orderId", params: { orderId: wo.id } });
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const b = Number(budget.replace(",", "."));
    if (!title.trim()) return setError("Titel angeben.");
    if (!Number.isFinite(b) || b < 0 || b > 1000) return setError("Budget zwischen 0 und 1000 $ angeben.");
    create.mutate();
  };
  if (project.isError) return <p className="form-error">{errorMessage(project.error)}</p>;
  const orders = (workOrders.data ?? []).filter((w) => w.projectId === projectId);
  const step = project.data ? nextStep(project.data, orders) : null;
  return (
    <>
      <section className="hero">
        <div className="hero-text">
          <p className="eyebrow">
            <Link to="/projects" className="link">
              Projekte
            </Link>
            <span className="chip chip--ghost">{project.data?.profileKey}</span>
            {project.data && <LifecycleControl key={project.data.lifecycle} project={project.data} />}
            {project.data && <FocusControl key={String(project.data.focus)} project={project.data} />}
            {project.data && !editing && (
              <button type="button" className="chip-btn" onClick={() => setEditing(true)}>
                <Icon name="edit" /> Bearbeiten
              </button>
            )}
          </p>
          <h1>{project.data?.title ?? "…"}</h1>
          <p className="hero-sub">{project.data?.goal}</p>
          {step && (
            <p className="hero-step" id="fokus">
              <NextStepLink
                step={step}
                projectId={projectId}
                project={project.data}
                onCriterion={(text) => {
                  setTitle(text.slice(0, 200));
                  setOrderForm(true);
                  requestAnimationFrame(() => titleRef.current?.focus());
                }}
              />
            </p>
          )}
        </div>
      </section>
      {project.data && <DonePanel project={project.data} />}
      <section className="panel panel--section" aria-labelledby="ordersHead">
        <div className="panel-head">
          <h2 id="ordersHead">Aufträge</h2>
          {!orderForm && (
            <button type="button" className="btn btn--primary" onClick={() => setOrderForm(true)}>
              <span className="btn-label">
                <Icon name="plus" /> Neuer Auftrag
              </span>
            </button>
          )}
        </div>
        {orderForm && (
          <form className="form capture" onSubmit={submit} noValidate aria-label="Neuer Auftrag">
            <label htmlFor={ids.title}>Titel</label>
            <input
              id={ids.title}
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="z. B. MVP v0.1"
            />
            <p className="muted">Klein halten: Ein Auftrag soll in wenigen Läufen fertig werden.</p>
            <label htmlFor={ids.budget}>Budget in $ (hartes Limit)</label>
            <input
              id={ids.budget}
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              inputMode="decimal"
            />
            <ErrorNote error={error} />
            <div className="form-actions">
              <button type="submit" className="btn btn--primary" disabled={create.isPending}>
                <span className="btn-label">
                  <Icon name="plus" /> Auftrag anlegen
                </span>
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => setOrderForm(false)}>
                Abbrechen
              </button>
            </div>
          </form>
        )}
        {orders.length === 0 && !orderForm && (
          <EmptyState
            icon="network"
            title="Noch keine Aufträge"
            text="Ein Auftrag beginnt mit dem Briefing und endet mit dem Release."
          />
        )}
        <ul className="order-list">
          {orders.map((w) => (
            <li key={w.id} className="order-row">
              <Link to="/work-orders/$orderId" params={{ orderId: w.id }} className="order-card">
                <span className="order-main">
                  <strong>{w.title}</strong>
                  <span className="muted">aktualisiert {dateTime(w.updatedAt)}</span>
                </span>
                <span className={`chip state-chip state-${orderStatus(w).cls}`}>{orderStatus(w).label}</span>
                <span className="mono muted">
                  {w.itemsDone}/{w.itemsTotal}
                </span>
              </Link>
              <Link
                to="/work-orders/$orderId"
                params={{ orderId: w.id }}
                hash="flow"
                className="icon-link"
                aria-label={`Flow Map: ${w.title}`}
                title="Flow Map"
              >
                <Icon name="network" />
              </Link>
            </li>
          ))}
        </ul>
      </section>
      {project.data?.deployApp && <ProjectRolloutPanel project={project.data} />}
      {project.data && <StocktakePanel project={project.data} />}
      {project.data && project.data.lifecycle !== "closed" && (
        <p className="muted fold-hint">
          Stand unklar? Die Bestandsaufnahme prüft das Repo, ohne Code zu ändern.{" "}
          <button
            type="button"
            className="btn btn--ghost"
            disabled={stocktake.isPending}
            onClick={() => stocktake.mutate()}
          >
            <span className="btn-label">
              <Icon name="search" /> Bestandsaufnahme anlegen
            </span>
          </button>
        </p>
      )}
      <details className="panel fold" open={editing} onToggle={(e) => setEditing(e.currentTarget.open)}>
        <summary>
          <h2>Einstellungen</h2> <span className="muted">Projektdaten, Repo, Auslieferung, Ausrollen</span>
        </summary>
        {editing && project.data && (
          <ProjectEditForm project={project.data} onDone={() => setEditing(false)} />
        )}
        <form className="form repo-panel" onSubmit={submitRepo} noValidate aria-labelledby="repoHead">
          <h3 id="repoHead">GitHub-Repo</h3>
          {project.data?.repoFullName ? (
            <p>
              <a
                className="link"
                href={`https://github.com/${project.data.repoFullName}`}
                target="_blank"
                rel="noreferrer noopener"
              >
                {project.data.repoFullName}
              </a>{" "}
              <span className="muted">
                Branch {project.data.repoDefaultBranch}. Läufe arbeiten dort und öffnen Pull Requests.
              </span>{" "}
              <button
                type="button"
                className="btn btn--ghost"
                disabled={setRepository.isPending}
                onClick={() => setRepository.mutate(null)}
              >
                Zuordnung lösen
              </button>
            </p>
          ) : (
            <>
              <p className="muted">
                Ohne Repo arbeiten Läufe in einem leeren Ordner. Die GitHub App muss im Repo installiert sein.
              </p>
              <label htmlFor={ids.repo}>Repo (owner/name)</label>
              <input
                id={ids.repo}
                value={repo}
                onChange={(e) => setRepo(e.target.value)}
                placeholder="medienrocker/meine-app"
                autoComplete="off"
                spellCheck={false}
              />
              <button type="submit" className="btn btn--primary" disabled={setRepository.isPending}>
                <span className="btn-label">
                  <Icon name="git" /> Repo zuordnen
                </span>
              </button>
            </>
          )}
          <ErrorNote error={repoError} />
        </form>
        {project.data && (
          <DeliveryPanel
            key={`${project.data.repoFullName}-${project.data.deliveryMode}-${project.data.deliverySteps.join("|")}`}
            project={project.data}
          />
        )}
        {project.data?.repoFullName && !project.data.deployApp && (
          <ProjectRolloutPanel project={project.data} />
        )}
      </details>
      {project.data && (
        <details className="panel fold material-panel">
          <summary>
            <h2>Material</h2> <span className="muted">Notizen, Links und Dateien für die Läufe</span>
          </summary>
          <MaterialPanel
            owner={{ type: "projects", id: projectId }}
            editable
            repoName={project.data.repoFullName}
            heading="h3"
          />
        </details>
      )}
    </>
  );
}
